// frontend/src/components/wizard/StepMapping/index.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useWizardStore } from '../../../store/useWizardStore';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { apiClient } from '../../../api/client';
import { ArrowRight, ArrowLeft, Loader2, X } from 'lucide-react';
import { Modal } from '../../Modal';
import { RuleBuilderModal } from './RuleBuilderModal';
import { MappingTopBar } from './MappingTopBar';
import { MappingTable } from './MappingTable';
import { DedupPanel } from './DedupPanel';
import { ImportPreviewModal } from './ImportPreviewModal';
import { SourceFieldPickerModal } from './SourceFieldPickerModal';
import { QualityRulesModal } from './QualityRulesModal';
import { ProfileModal } from '../../ProfileModal';
import {
  buildExportPayload, downloadJSON, downloadExcel,
  parseImportFile, validateImportedMapping, applyImportToLocalMap,
} from '../../../utils/mappingIO';

// ── Constante única de motores planos ────────────────────────────────
// BUG FIX: antes estaba definida dos veces (aquí y dentro de fetchMissingSchemas).
// MongoDB NO está aquí — se trata diferente según dynamic_schema del backend.
const FLAT_TYPES = ['csv', 'excel', 'dbf', 'googlesheets'];

export default function StepMapping() {
  const {
    source, target,
    mapping, setMapping,
    nextStep, prevStep,
    dedupConfig, setDedupConfig,
    sourceColumns, setSourceColumns,
    targetColumns, setTargetColumns,
    syncMode, editId,
    sourceSelectedTables, targetSelectedTables, sourceRelationships, targetRelationships // N:M
  } = useWizardStore();

  const { t } = useLanguageStore();

  // ── Target type detection (used across effects, handlers, and render) ──
  const isTargetFlat = FLAT_TYPES.includes(target?.type?.toLowerCase());
  const isTargetMongo = target?.type?.toLowerCase() === 'mongodb';
  const isTargetRestApi = target?.type?.toLowerCase() === 'rest_api';
  const isTargetFlexible = isTargetFlat || isTargetMongo;

  const [isLoading, setIsLoading] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);
  const [isAiMatching, setIsAiMatching] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [localMap, setLocalMap] = useState({});
  const [activeTransform, setActiveTransform] = useState(null);
  const [activeTarget, setActiveTarget] = useState(null);
  // const [selectedTabIdx, setSelectedTabIdx] = useState(0);  // 🆕 Para tabs en N:M
  const [modalState, setModalState] = useState({
    isOpen: false, title: '', message: '', onAccept: null, showCancel: true,
  });

  // ── Filtros visuales de columnas (no afectan localMap ni Zustand) ──
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // ── Import/Export state ──────────────────────────────────────────────
  const [importPreview, setImportPreview] = useState(null);   // { validationResult, fileName }
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [qualityRulesTarget, setQualityRulesTarget] = useState(null);
  const [showProfileForMapping, setShowProfileForMapping] = useState(false);
  const [infoModal, setInfoModal] = useState({ isOpen: false, title: '', message: '' });

  // Guardia: el auto-match AI solo corre 1 vez por sesión de mapping
  const autoAiRanRef = useRef(false);
  const hydrationDoneRef = useRef(false);

  const closeModal = () => setModalState(prev => ({ ...prev, isOpen: false }));

  // ═══════════════════════════════════════════════════════════════════
  // AUTO-HIDRATACIÓN DE ESQUEMAS (paralela, no bloqueante)
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    const fetchMissingSchemas = async () => {
      if (!source?.config || !target?.config) return;
      if (hydrationDoneRef.current) return;

      const isSourceFlat = FLAT_TYPES.includes(source.type?.toLowerCase());

      const needsSource = (sourceColumns?.length ?? 0) === 0;
      const needsTarget = (targetColumns?.length ?? 0) === 0 && !isTargetRestApi;
      if (!needsSource && !needsTarget) return;

      setIsHydrating(true);

      // ── FETCH ORIGEN ──────────────────────────────────────────────
      const fetchSource = async () => {
        if (!needsSource) return;

        const PLACEHOLDER_TYPES = ['csv', 'dbf'];
        const isSourceFlat_ = PLACEHOLDER_TYPES.includes(source.type?.toLowerCase());
        // 🔧 Guard: si no hay ninguna tabla configurada, no intentar fetch
        // Evita el error "Error al leer archivo" cuando el usuario borra todas las tablas
        const hasSourceTable = (sourceSelectedTables?.length > 0)
          || (source?.config?.selectedTable?.trim());
        if (!isSourceFlat_ && !hasSourceTable) return;

        try {
          // 🔧 N:M/N:1: si hay múltiples tablas origen, fetch por tabla y prefijar "tabla.col"
          if (!isSourceFlat_ && sourceSelectedTables?.length > 1) {
            const allCols = [];
            for (const tableName of sourceSelectedTables) {
              // const res = await apiClient.post('/onboarding/columns', {
              //   type: source.type,
              //   config: { ...source.config, selectedTable: tableName },
              // });
              const res = await apiClient.post('/onboarding/columns', {
                type: source.type,
                config: {
                  ...source.config, selectedTable: tableName,
                  ...(editId && { _idConfigHead: editId, _side: 'source' }),
                },
              });
              if (res.data.success) {
                res.data.columns.forEach(col =>
                  allCols.push({ ...col, name: `${tableName}.${col.name}` })
                );
              }
            }
            setSourceColumns(allCols);
            return;
          }

          // 1:1 / N:1 origen simple — comportamiento original
          const tableSrc = isSourceFlat_
            ? 'Datos del Archivo'
            : (source.config.selectedTable || sourceSelectedTables?.[0] || '');

          // const res = await apiClient.post('/onboarding/columns', {
          //   type: source.type,
          //   config: { ...source.config, selectedTable: tableSrc },
          // });
          const res = await apiClient.post('/onboarding/columns', {
            type: source.type,
            config: {
              ...source.config, selectedTable: tableSrc,
              ...(editId && { _idConfigHead: editId, _side: 'source' }),
            },
          });
          if (res.data.success) setSourceColumns(res.data.columns);

        } catch (error) {
          if (editId && mapping?.length > 0) {
            const recovered = [...new Set(
              mapping.filter(m => m.source && m.source !== 'IGNORE').map(m => m.source)
            )].map(name => ({ name }));
            setSourceColumns(recovered);
          } else {
            setGlobalError(t('wizard.mapping.errors.hydration_failed_db') ||
              'Error al conectar con la base de datos de origen.');
          }
        }
      };

      // ── FETCH DESTINO ─────────────────────────────────────────────
      const fetchTarget = async () => {
        if (!needsTarget) return;

        // ── Destino BD (SQL / NOSQL) ──────────────────────────────────
        if (!isTargetFlat) {
          try {
            // 🔧 N:M/1:N: si hay múltiples tablas destino, fetch por tabla y prefijar
            if (targetSelectedTables?.length > 1) {
              const allCols = [];
              for (const tableName of targetSelectedTables) {
                // const res = await apiClient.post('/onboarding/columns', {
                //   type: target.type,
                //   config: { ...target.config, selectedTable: tableName },
                // });
                const res = await apiClient.post('/onboarding/columns', {
                  type: target.type,
                  config: {
                    ...target.config, selectedTable: tableName,
                    ...(editId && { _idConfigHead: editId, _side: 'target' }),
                  },
                });
                if (res.data.success) {
                  res.data.columns.forEach(col =>
                    allCols.push({ ...col, name: `${tableName}.${col.name}` })
                  );
                }
              }
              setTargetColumns(allCols);
            } else {
              // const res = await apiClient.post('/onboarding/columns', {
              //   type: target.type,
              //   config: { ...target.config, selectedTable: target.config.selectedTable },
              // });
              const res = await apiClient.post('/onboarding/columns', {
                type: target.type,
                config: {
                  ...target.config, selectedTable: target.config.selectedTable,
                  ...(editId && { _idConfigHead: editId, _side: 'target' }),
                },
              });

              if (res.data.success) {
                // ── BUG FIX: MongoDB con colección vacía/nueva ─────────────
                // dynamic_schema=true → el backend no encontró campos definidos.
                // Tratamos igual que un destino plano: espejamos desde origen.
                // El usuario puede renombrar los campos vía el input de alias
                // en MappingTable (que ya es editable para MongoDB isMongo=true).
                if (res.data.dynamic_schema && res.data.columns.length === 0) {
                  setTargetColumns([]);   // El useEffect de localMap espejará desde source
                  setGlobalError(
                    'ℹ️ ' + (t('wizard.mapping.mongodb_dynamic') ||
                      'Colección nueva: los campos se crearán al migrar. Puedes renombrar las columnas.')
                  );
                } else {
                  setTargetColumns(res.data.columns);
                }
              }
            }
          } catch (error) {
            if (editId && mapping?.length > 0) {
              setTargetColumns(mapping.map(m => ({
                name: m.target, type: 'VARCHAR', nullable: true,
                autoincrement: false, has_default: false,
              })));
              setGlobalError(
                t('wizard.mapping.errors.hydration_fallback') ||
                'ℹ️ Mostrando configuración guardada.'
              );
            } else {
              setGlobalError(
                t('wizard.mapping.errors.hydration_failed_db') ||
                'Error al conectar con la base de datos de destino.'
              );
            }
          }

          // ── Destino Archivo Plano (CSV / Excel / Google Sheets / DBF) ──
        } else {
          if (editId && mapping?.length > 0) {
            // Edición: reconstruir desde mapping guardado
            setTargetColumns(mapping.map(m => ({
              name: m.target, type: 'VARCHAR', nullable: true, autoincrement: false,
            })));
          } else {
            // Creación nueva: intentar leer si ya existe (Sheets/Excel con datos previos)
            const SHEET_TYPES = ['excel', 'googlesheets'];
            const isSheetType = SHEET_TYPES.includes(target.type?.toLowerCase());
            let readSuccess = false;

            if (isSheetType && target.config?.host) {
              try {
                // const res = await apiClient.post('/onboarding/columns', {
                //   type: target.type,
                //   config: { ...target.config, selectedTable: target.config.selectedTable },
                // });
                const res = await apiClient.post('/onboarding/columns', {
                  type: target.type,
                  config: {
                    ...target.config, selectedTable: target.config.selectedTable,
                    ...(editId && { _idConfigHead: editId, _side: 'target' }),
                  },
                });
                if (res.data.success && res.data.columns?.length > 0) {
                  setTargetColumns(res.data.columns);
                  readSuccess = true;
                }
              } catch (e) {
                // El archivo/hoja no existe aún — espejo desde origen (ver abajo)
              }
            }
            // Si no se leyó: targetColumns queda vacío → el useEffect de localMap
            // detecta isTargetFlat + sourceColumns → espeja automáticamente.
          }
        }
      };

      await Promise.allSettled([fetchSource(), fetchTarget()]);
      hydrationDoneRef.current = true;
      setIsHydrating(false);
    };

    fetchMissingSchemas();
  }, [source, target, sourceColumns?.length, targetColumns?.length, mapping, editId]);


  // ═══════════════════════════════════════════════════════════════════
  // INICIALIZACIÓN DE localMap
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Espejo desde origen para:
    //   • Destinos FLAT nuevos (CSV, Excel, etc.) sin targetColumns aún
    //   • MongoDB nuevos con colección vacía (dynamic_schema → targetColumns = [])
    //   • REST API targets sin schema introspectable (POST-only endpoints)
    const shouldMirror = (isTargetFlat || isTargetMongo || isTargetRestApi) &&
      (targetColumns?.length ?? 0) === 0 &&
      (sourceColumns?.length ?? 0) > 0 &&
      (!editId || (mapping?.length ?? 0) === 0) &&
      !isHydrating;

    if (shouldMirror) {
      setTargetColumns(sourceColumns.map(col => ({
        name: col.name,
        type: col.type || 'VARCHAR',
        nullable: true,
        autoincrement: false,
        has_default: false,
      })));
      return;
    }

    if (Object.keys(localMap).length === 0 && (targetColumns?.length ?? 0) > 0 && !isHydrating) {
      const initialMap = {};

      const scoreCol = (col) => {
        if (col.autoincrement) return 6;
        if (col.is_pk) return 1;
        if (col.is_fk) return 2;
        if (!col.nullable && !col.has_default) return 3;
        if (!col.nullable && col.has_default) return 4;
        return 5;
      };
      const sortedCols = [...targetColumns].sort((a, b) => scoreCol(a) - scoreCol(b));

      sortedCols.forEach(tc => {
        const existing = mapping.find(m => m.target === tc.name);
        if (existing) {
          initialMap[tc.name] = {
            source: existing.source,
            type: existing.type || (existing.source === 'IGNORE' ? 'ignore' : 'direct'),
            pipeline: existing.pipeline || [],
            qualityRules: existing.qualityRules || [],
            confidence: existing.confidence || null,
          };
        } else if (isTargetFlat || isTargetMongo) {
          const sameSource = sourceColumns?.find(sc => sc.name === tc.name);
          initialMap[tc.name] = {
            source: sameSource ? tc.name : '',
            type: sameSource ? 'direct' : 'unmapped',
            pipeline: [],
            confidence: sameSource ? 100 : null,
          };
        } else {
          initialMap[tc.name] = {
            source: '', type: 'unmapped', pipeline: [], confidence: null,
          };
        }
      });

      setLocalMap(initialMap);
    }
  }, [targetColumns, mapping, sourceColumns, editId, target, isHydrating]);


  // ═══════════════════════════════════════════════════════════════════
  // AUTO-AI: dispara solo 1 vez para BD→BD nuevo
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    const hasLocalMap = Object.keys(localMap).length > 0;
    const hasUnmapped = Object.values(localMap).some(m => m.type === 'unmapped');

    if (!editId && !isTargetFlat && hasLocalMap && hasUnmapped &&
      (sourceColumns?.length ?? 0) > 0 && (targetColumns?.length ?? 0) > 0 &&
      !autoAiRanRef.current) {
      autoAiRanRef.current = true;
      handleAiMatch();
    }
  }, [localMap, editId, target, sourceColumns, targetColumns]);


  // ═══════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════

  // En handleTabNavigation (se pasa a MappingTable):
  const handleTabNavigation = (e, idx) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const next = document.querySelector(`[data-alias-idx="${idx + 1}"]`);
    if (next) next.focus();
  };

  // ── AI Match ─────────────────────────────────────────────────────
  const handleAiMatch = async () => {
    setIsAiMatching(true);
    setGlobalError('');
    try {
      const response = await apiClient.post('/ai-mapping/match', {
        source_columns: sourceColumns.map(c => c.name),
        target_columns: targetColumns.map(c => c.name),
      });
      const suggestions = response.data.matches;

      setLocalMap(prev => {
        const newMap = { ...prev };
        suggestions.forEach(match => {
          if (newMap[match.target]?.type === 'unmapped') {
            newMap[match.target] = {
              ...newMap[match.target],
              source: match.source,
              type: 'direct',
              pipeline: [],
              confidence: match.confidence || 95,
            };
          }
        });
        return newMap;
      });
    } catch (err) {
      // Si el interceptor ya lo manejó (modal de quota), no duplicar
      if (err._quotaHandled) return;

      const detail = err.response?.data?.detail;
      const errorMsg = (typeof detail === 'string')
        ? detail
        : (detail?.code ? t(`errors.${detail.code}`) : null);
      setGlobalError(
        errorMsg ||
        t('wizard.mapping.errors.ai_failed') ||
        'No se pudo completar el mapeo inteligente.'
      );
    } finally {
      setIsAiMatching(false);
    }
  };

  // ── Acción Global — aplica una regla a todos los campos mapeados ──
  // BUG FIX: esta función no existía → applyGlobalRule era undefined → crash al usar
  // el menú "Acciones Globales" en MappingTopBar.
  const applyGlobalRule = useCallback((ruleId, label) => {
    setLocalMap(prev => {
      const newMap = { ...prev };
      Object.keys(newMap).forEach(targetCol => {
        const col = newMap[targetCol];
        // Solo aplica a columnas activamente mapeadas (no ignoradas ni sin asignar)
        if (col.type !== 'ignore' && col.type !== 'unmapped' && col.source) {
          const alreadyHasRule = col.pipeline?.some(r => r.id === ruleId);
          if (!alreadyHasRule) {
            newMap[targetCol] = {
              ...col,
              type: 'transform',
              pipeline: [...(col.pipeline || []), { id: ruleId, label, params: {} }],
            };
          }
        }
      });
      return newMap;
    });
  }, []);

  // ── Export JSON ───────────────────────────────────────────────────
  const handleExportJSON = useCallback(() => {
    const wizardMode = sourceSelectedTables?.length > 1 || targetSelectedTables?.length > 1
      ? 'N:M' : '1:1';
    const payload = buildExportPayload(
      localMap, wizardMode, '', targetSelectedTables, targetColumns
    );
    const safeName = (target?.config?.selectedTable || 'mapping').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadJSON(payload, `datiaflow_mapping_${safeName}`);
  }, [localMap, sourceSelectedTables, targetSelectedTables, targetColumns, target]);

  // ── Export Excel ──────────────────────────────────────────────────
  const handleExportExcel = useCallback(() => {
    const wizardMode = sourceSelectedTables?.length > 1 || targetSelectedTables?.length > 1
      ? 'N:M' : '1:1';
    const payload = buildExportPayload(
      localMap, wizardMode, '', targetSelectedTables, targetColumns
    );
    const safeName = (target?.config?.selectedTable || 'mapping').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadExcel(payload, `datiaflow_mapping_${safeName}`);
  }, [localMap, sourceSelectedTables, targetSelectedTables, targetColumns, target]);

  // ── Import file ───────────────────────────────────────────────────
  const handleImportFile = useCallback(async (file) => {
    setGlobalError('');
    const result = await parseImportFile(file);
    const { entries, mode, error } = result;

    if (error) {
      let errorMsg;
      if (error === 'INVALID_HEADERS') {
        const info = result.invalidHeaders || {};
        if (info.missing) {
          errorMsg = (t('wizard.mapping.import_err_missing_header') || 'Columna obligatoria no encontrada: {{header}}')
            .replace('{{header}}', info.missing);
        } else if (info.unknown?.length) {
          errorMsg = (t('wizard.mapping.import_err_unknown_headers') || 'Columnas no reconocidas: {{headers}}. Esperadas: {{expected}}')
            .replace('{{headers}}', info.unknown.join(', '))
            .replace('{{expected}}', (info.expected || []).join(', '));
        }
      }

      if (!errorMsg) {
        const errorMessages = {
          UNSUPPORTED_FORMAT: t('wizard.mapping.import_err_format') || 'Formato no soportado. Usa JSON o Excel (.xlsx).',
          INVALID_STRUCTURE: t('wizard.mapping.import_err_structure') || 'El archivo no tiene la estructura esperada.',
          PARSE_ERROR: t('wizard.mapping.import_err_parse') || 'Error al leer el archivo. Verifica que no esté corrupto.',
          EMPTY_MAPPINGS: t('wizard.mapping.import_err_empty') || 'El archivo no contiene mappings.',
        };
        errorMsg = errorMessages[error] || errorMessages.PARSE_ERROR;
      }

      setGlobalError(errorMsg);
      return;
    }

    if (entries.length === 0) {
      setGlobalError(t('wizard.mapping.import_err_empty') || 'El archivo no contiene mappings.');
      return;
    }

    const validationResult = validateImportedMapping(entries, sourceColumns, targetColumns);
    setImportPreview({ validationResult, fileName: file.name });
  }, [sourceColumns, targetColumns, t]);

  // ── Confirmar import ──────────────────────────────────────────────
  const handleConfirmImport = useCallback(() => {
    if (!importPreview?.validationResult) return;
    const newMap = applyImportToLocalMap(localMap, importPreview.validationResult.matched);
    setLocalMap(newMap);
    setImportPreview(null);
  }, [importPreview, localMap]);

  // ── Agregar campos del origen al destino (flat / MongoDB) ─────────
  const availableSourceFields = useMemo(() => {
    if (!isTargetFlexible || !sourceColumns?.length) return [];
    const targetNames = new Set(
      (targetColumns || []).map(tc => tc.name.toLowerCase())
    );
    return sourceColumns.filter(sc => !targetNames.has(sc.name.toLowerCase()));
  }, [isTargetFlexible, sourceColumns, targetColumns]);

  const handleAddSourceFields = useCallback((selectedCols) => {
    // Operación atómica: targetColumns + localMap en una sola pasada
    const newTargetCols = selectedCols.map(sc => ({
      name: sc.name,
      type: sc.type || 'VARCHAR',
      nullable: true,
      autoincrement: false,
      has_default: false,
    }));
    setTargetColumns([...(targetColumns || []), ...newTargetCols]);

    setLocalMap(prev => {
      const updated = { ...prev };
      selectedCols.forEach(sc => {
        updated[sc.name] = {
          source: '', type: 'unmapped', pipeline: [], confidence: null,
        };
      });
      return updated;
    });

    setShowFieldPicker(false);
  }, [targetColumns, setTargetColumns]);

  // ── S5.2: Profiling → Quality Rules desde mapping ─────────────────
  const mappedColumnsForProfile = useMemo(() => {
    if (!localMap || !sourceColumns?.length) return [];
    return Object.entries(localMap)
      .filter(([, data]) =>
        data.type !== 'unmapped' && data.type !== 'ignore' &&
        data.source && !data.pipeline?.some(r => r.id === 'SET_FIXED' || r.id === 'SET_CURRENT_DATE')
      )
      .map(([targetField, data]) => {
        const src = data.source;
        // For N:M, extract table name from "tabla.col" prefix
        const sourceTable = src.includes('.') ? src.split('.')[0] : null;
        return { sourceField: src, targetField, sourceTable };
      });
  }, [localMap, sourceColumns]);

  // Source table for profiling: first mapped source table, or single-table config
  const profileSourceTable = useMemo(() => {
    if (sourceSelectedTables?.length === 1) return sourceSelectedTables[0];
    const firstMapped = mappedColumnsForProfile.find(mc => mc.sourceTable);
    return firstMapped?.sourceTable || source?.config?.selectedTable || '';
  }, [mappedColumnsForProfile, sourceSelectedTables, source]);

  const handleAcceptProfileRules = useCallback((rules) => {
    // rules = [{ targetField, rules: [{ type, mode, config }] }]
    setLocalMap(prev => {
      const updated = { ...prev };
      rules.forEach(({ targetField, rules: newRules }) => {
        if (!updated[targetField]) return;
        const existing = updated[targetField].qualityRules || [];
        const existingTypes = new Set(existing.map(r => r.type));

        // Merge: skip rules that already exist by type
        const toAdd = newRules.filter(r => !existingTypes.has(r.type));
        if (toAdd.length === 0) return;

        updated[targetField] = {
          ...updated[targetField],
          qualityRules: [...existing, ...toAdd],
        };
      });
      return updated;
    });
  }, []);

  // ── Guardar mapping en Zustand ────────────────────────────────────
  const saveToStore = () => {
    const finalMapping = Object.entries(localMap)
      .filter(([, data]) => data.type !== 'unmapped' && (data.source !== '' || data.pipeline?.length > 0))
      .map(([targetCol, data]) => ({
        target: targetCol,
        source: data.type === 'ignore' ? 'IGNORE' : data.source,
        type: data.type,
        pipeline: data.pipeline || [],
        qualityRules: data.qualityRules || [],
        confidence: data.confidence,
      }));
    setMapping(finalMapping);
  };

  // ── Continuar al siguiente paso ───────────────────────────────────
  const handleNextStep = () => {
    setGlobalError('');
    saveToStore();

    // 🔧 Validar dedup: si está activado, exigir que se seleccione una columna
    if (dedupConfig.enabled && !dedupConfig.column) {
      setGlobalError(
        t('wizard.mapping.errors.dedup_column_required') ||
        'Debes seleccionar una columna para "Evitar duplicados".'
      );
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      return;
    }

    const isTargetFlatFile = [...FLAT_TYPES, 'mongodb'].includes(target?.type?.toLowerCase());

    if (!isTargetFlatFile) {
      const unmappedMandatory = targetColumns.find(tc => {
        const isMandatory = !tc.nullable && !tc.autoincrement;
        const mappingStatus = localMap[tc.name];
        return isMandatory && (!mappingStatus || mappingStatus.type === 'unmapped');
      });

      if (unmappedMandatory) {
        setGlobalError(t('wizard.mapping.errors.missing_mandatory'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    nextStep();
  };

  // ── Métricas de progreso ──────────────────────────────────────────
  const totalCols = targetColumns?.length || 0;
  const mappedCols = Object.values(localMap).filter(m => m.type !== 'unmapped' && m.type !== 'ignore').length;
  const ignoredCols = Object.values(localMap).filter(m => m.type === 'ignore').length;
  const unmappedCols = Object.values(localMap).filter(m => m.type === 'unmapped').length;
  const decidedCols = mappedCols + ignoredCols;
  const progressPercent = totalCols === 0 ? 0 : Math.round((decidedCols / totalCols) * 100);

  // ── Columnas filtradas visualmente (no modifica localMap) ──────────
  const filteredTargetColumns = React.useMemo(() => {
    let filtered = targetColumns || [];

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter(tc => {
        const conf = localMap[tc.name];
        const srcName = (conf?.source || '').toLowerCase();
        return tc.name.toLowerCase().includes(q) || srcName.includes(q);
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(tc => {
        const type = localMap[tc.name]?.type || 'unmapped';
        if (statusFilter === 'mapped') return type !== 'unmapped' && type !== 'ignore';
        if (statusFilter === 'unmapped') return type === 'unmapped';
        if (statusFilter === 'ignored') return type === 'ignore';
        return true;
      });
    }

    return filtered;
  }, [targetColumns, localMap, searchText, statusFilter]);

  // ── Loader de hidratación ─────────────────────────────────────────
  if (isHydrating) {
    return (
      <div className="w-full max-w-6xl mx-auto pb-12 flex flex-col items-center justify-center min-h-[400px] animate-in fade-in duration-500">
        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
        <h3 className="text-xl font-bold text-slate-800">
          {t('wizard.mapping.hydrating') || 'Leyendo esquemas en tiempo real...'}
        </h3>
        <p className="text-sm text-slate-500 mt-2">
          {t('wizard.mapping.hydrating_desc') ||
            'Conectando con el origen y destino para obtener las columnas físicas.'}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto pb-12 animate-in fade-in duration-500">

      <div className="mb-6 flex justify-between items-end">
        <div>
          <h2 className="text-2xl font-black text-slate-900">
            {t('wizard.mapping.title') || 'Mapeo de Datos'}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {t('wizard.mapping.subtitle') || 'Conecta el origen con el destino'}
          </p>
        </div>
      </div>

      {globalError && (
        <div className={`mb-6 p-4 text-sm font-bold rounded-2xl flex items-center justify-between shadow-sm border ${globalError.includes('ℹ️')
          ? 'bg-blue-50 text-blue-700 border-blue-200'
          : 'bg-red-50 text-red-600 border-red-100'
          }`}>
          <div className="flex items-center">
            <span className="mr-2">{globalError.includes('ℹ️') ? '💡' : '⚠️'}</span>
            {globalError}
          </div>
          <button
            onClick={() => setGlobalError('')}
            className="ml-3 p-1 hover:bg-black/5 rounded-lg transition shrink-0"
            title={t('common.buttons.close') || 'Cerrar'}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* BUG FIX: applyGlobalRule ahora se pasa correctamente */}
      <MappingTopBar
        handleAiMatch={handleAiMatch}
        isAiMatching={isAiMatching}
        applyGlobalRule={applyGlobalRule}
        handleExportJSON={handleExportJSON}
        handleExportExcel={handleExportExcel}
        handleImportFile={handleImportFile}
        onAddSourceFields={() => setShowFieldPicker(true)}
        availableSourceCount={availableSourceFields.length}
        showAddSourceFields={isTargetFlexible}
        onProfileMapping={async () => {
          try {
            const res = await apiClient.get('/client/top-values-enabled');
            if (!res.data?.enabled) {
              setInfoModal({
                isOpen: true,
                title: t('profiling.top_values_disabled_title') || 'Visualización de datos reales desactivada',
                message: t('profiling.top_values_disabled_msg') || 'Por su protección, la visualización de datos reales (valores más frecuentes) está desactivada por defecto. Para activarla, vaya a Mi Cuenta > Plan y active la opción "Visualizar datos reales en perfilado".',
              });
              return;
            }
            setShowProfileForMapping(true);
          } catch { setShowProfileForMapping(true); }
        }}
        mappedCount_forProfile={mappedColumnsForProfile.length}
        handleClear={() => {
          const clearedMap = {};
          Object.keys(localMap).forEach(k => {
            clearedMap[k] = { ...localMap[k], source: '', type: 'unmapped', pipeline: [], confidence: null };
          });
          setLocalMap(clearedMap);
        }}
        handleIgnoreUnmapped={() => {
          setLocalMap(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(k => {
              if (updated[k].type === 'unmapped') {
                updated[k] = { ...updated[k], source: 'IGNORE', type: 'ignore', pipeline: [], confidence: null };
              }
            });
            return updated;
          });
        }}
        mappedCount={mappedCols}
        ignoredCount={ignoredCols}
        unmappedCount={unmappedCols}
        total={totalCols}
        searchText={searchText}
        setSearchText={setSearchText}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        filteredCount={filteredTargetColumns.length}
      />

      {/* 🔧 HEADER DE FLUJO N:M + TABS DE NAVEGACIÓN */}
      {sourceSelectedTables && sourceSelectedTables.length > 0 && (
        <div className="mb-4">

          {/* Línea de flujo: [DESTINO(S) con JOIN] ← [ORIGEN(ES) con JOIN]
              Coherente con la tabla: izquierda=Target, derecha=Source */}
          <div className="flex items-center gap-1.5 flex-wrap mb-2 px-1 font-mono text-[13px]">

            {/* DESTINO — izquierda */}
            {(targetSelectedTables.length > 0
              ? targetSelectedTables
              : [target?.config?.selectedTable]
            ).filter(Boolean).map((t, i) => {
              const rel = targetRelationships?.[i - 1];
              return (
                <React.Fragment key={t}>
                  {i > 0 && (
                    <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded uppercase border border-emerald-100">
                      {rel?.joinType || 'JOIN'}
                    </span>
                  )}
                  <span className="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                    {t}
                  </span>
                </React.Fragment>
              );
            })}

            {/* Flecha */}
            <span className="text-slate-500 font-bold px-1">←</span>

            {/* ORIGEN — derecha */}
            {sourceSelectedTables.map((srcTable, i) => {
              const rel = sourceRelationships?.[i - 1];
              return (
                <React.Fragment key={srcTable}>
                  {i > 0 && (
                    <span className="text-[10px] font-bold text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded uppercase border border-indigo-100">
                      {rel?.joinType || 'JOIN'}
                    </span>
                  )}
                  <span className="font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-200">
                    {srcTable}
                  </span>
                </React.Fragment>
              );
            })}
          </div>

          {/* Tabs de navegación — solo si hay más de 1 tabla origen */}
          {/* {sourceSelectedTables.length > 1 && (
            <div className="border-b border-slate-200">
              <div className="flex gap-1 bg-slate-50 px-2 pt-2 overflow-x-auto">
                {sourceSelectedTables.map((srcTable, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedTabIdx(idx)}
                    className={`px-4 py-2 rounded-t-lg font-medium text-sm whitespace-nowrap transition-colors ${selectedTabIdx === idx
                        ? 'bg-white text-indigo-600 border-b-2 border-indigo-600'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                  >
                    {srcTable}
                  </button>
                ))}
              </div>
            </div>
          )} */}

        </div>
      )}

      <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
        <MappingTable
          syncMode={syncMode}
          // targetColumns={targetColumns || []}
          targetColumns={filteredTargetColumns}
          sourceColumns={sourceColumns || []}
          localMap={localMap}
          target={target}
          activeTarget={activeTarget}
          setActiveTarget={setActiveTarget}
          setActiveTransform={setActiveTransform}
          handleAliasChange={(targetCol, val) => {
            if (!val || val.trim() === targetCol) return;
            const trimmed = val.trim();

            // 1. Re-keyear localMap: la key ES el nombre del campo destino
            setLocalMap(prev => {
              const { [targetCol]: colData, ...rest } = prev;
              return { ...rest, [trimmed]: { ...colData } };
            });

            // 2. Actualizar targetColumns en Zustand para persistir al volver de Review
            setTargetColumns(
              (targetColumns || []).map(tc =>
                tc.name === targetCol ? { ...tc, name: trimmed } : tc
              )
            );
          }}
          // handleSelectChange={(targetCol, val) =>
          //   setLocalMap(prev => ({
          //     ...prev,
          //     [targetCol]: {
          //       ...prev[targetCol],
          //       source: val,
          //       type: val === 'IGNORE' ? 'ignore' : (val ? 'direct' : 'unmapped'),
          //       confidence: null,   // Selección manual limpia el % del AI
          //     },
          //   }))
          // }
          handleSelectChange={(targetCol, val) => {
            // Detectar selección de ID auto-generado (new_id_ref)
            // Formato especial del valor: "__NEW_ID__:tabla.columna"
            const isNewIdRef = val?.startsWith('__NEW_ID__:');
            const refPayload = isNewIdRef ? val.replace('__NEW_ID__:', '') : null;
            const [refTable, refNewPK] = refPayload ? refPayload.split('.') : [null, null];

            // Buscar en targetRelationships el bridgeColumn correspondiente
            const fkRel = isNewIdRef
              ? (targetRelationships || []).find(r => r.tableFrom === refTable)
              : null;

            setLocalMap(prev => ({
              ...prev,
              [targetCol]: {
                ...prev[targetCol],
                source: isNewIdRef ? refPayload : val,
                type: isNewIdRef ? 'new_id_ref'
                  : val === 'IGNORE' ? 'ignore'
                    : val ? 'direct'
                      : 'unmapped',
                confidence: null,
                // Metadatos para el motor de resolución (solo new_id_ref)
                ...(isNewIdRef && {
                  refTable: refTable,
                  refNewPK: refNewPK,
                  refBridge: fkRel?.bridgeColumn || '',
                }),
              },
            }));
          }}
          targetRelationships={targetRelationships}  // ← nueva prop
          onOpenQualityRules={(targetCol) => setQualityRulesTarget(targetCol)}
          // handleTabNavigation={() => { }}
          handleTabNavigation={handleTabNavigation}
        />
      </div>

      <DedupPanel
        dedupConfig={dedupConfig}
        setDedupConfig={setDedupConfig}
        sourceColumns={sourceColumns || []}
      />

      <div className="mt-8 flex justify-between items-center border-t border-slate-200 pt-6">
        <button
          type="button"
          onClick={() => { saveToStore(); prevStep(); }}
          className="px-6 py-2.5 text-slate-600 font-bold hover:bg-slate-100 rounded-xl transition flex items-center"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> {t('common.buttons.back')}
        </button>

        <button
          type="button"
          onClick={handleNextStep}
          disabled={progressPercent < 100}
          className="px-8 py-2.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition shadow-md flex items-center disabled:opacity-50"
        >
          {t('common.buttons.review')} <ArrowRight className="w-5 h-5 ml-2" />
        </button>
      </div>

      <Modal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        onClose={closeModal}
        onAccept={modalState.onAccept}
        showCancel={modalState.showCancel}
      />

      {activeTransform && (
        <RuleBuilderModal
          activeTransform={activeTransform}
          localMap={localMap}
          setLocalMap={setLocalMap}
          onClose={() => setActiveTransform(null)}
          onClearPipeline={() =>
            setLocalMap(prev => {
              const colData = prev[activeTransform];
              return {
                ...prev,
                [activeTransform]: {
                  ...colData,
                  type: (colData.source && colData.source !== 'IGNORE') ? 'direct' : 'unmapped',
                  pipeline: [],
                  confidence: null,
                },
              };
            })
          }
          onForceModalRequest={(action) => {
            setModalState({
              isOpen: true,
              title: t('wizard.mapping.replace_rule_title'),
              message: t('wizard.mapping.replace_rule_msg'),
              showCancel: true,
              onAccept: () => { action(); closeModal(); },
            });
          }}
          targetAvailableTables={target?.config?.availableTables || []}
          onFetchLookupColumns={async (tableName) => {
            try {
              const res = await apiClient.post('/onboarding/columns', {
                type: target.type,
                config: { ...target.config, selectedTable: tableName },
              });
              return res.data?.columns || [];
            } catch {
              return [];
            }
          }}
          sourceColumns={sourceColumns || []}
          source={source}
          target={target}
        />
      )}

      {importPreview && (
        <ImportPreviewModal
          isOpen={true}
          onClose={() => setImportPreview(null)}
          onConfirm={handleConfirmImport}
          validationResult={importPreview.validationResult}
          fileName={importPreview.fileName}
        />
      )}

      {showFieldPicker && (
        <SourceFieldPickerModal
          isOpen={true}
          onClose={() => setShowFieldPicker(false)}
          onConfirm={handleAddSourceFields}
          availableColumns={availableSourceFields}
        />
      )}
      {qualityRulesTarget && (
        <QualityRulesModal
          isOpen={true}
          onClose={() => setQualityRulesTarget(null)}
          targetField={qualityRulesTarget}
          qualityRules={localMap[qualityRulesTarget]?.qualityRules || []}
          onSave={(rules) => {
            setLocalMap(prev => ({
              ...prev,
              [qualityRulesTarget]: {
                ...prev[qualityRulesTarget],
                qualityRules: rules,
              },
            }));
            setQualityRulesTarget(null);
          }}
        />
      )}

      {showProfileForMapping && (
        <ProfileModal
          isOpen={true}
          onClose={() => setShowProfileForMapping(false)}
          sourceConfig={{
            engine: source?.type,
            type: source?.type,
            config: source?.config,
          }}
          tableName={profileSourceTable}
          idConfigHead={editId || null}
          mode="mapping"
          mappedColumns={mappedColumnsForProfile}
          onAcceptRules={handleAcceptProfileRules}
        />
      )}
      {/* OBS-09: TopValues disabled info modal */}
      {infoModal.isOpen && (
        <Modal isOpen={true} title={infoModal.title} message={infoModal.message}
          onAccept={() => setInfoModal({ isOpen: false, title: '', message: '' })}
          onClose={() => setInfoModal({ isOpen: false, title: '', message: '' })}
          showCancel={false} />
      )}
    </div>
  );
}